import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiHandler,
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { FreightQuoteRequest, FreightQuoteResponse, getFreightQuote } from "@tCentro/Services/CENPDPFreightQuoteService";
import { addCatRequest, addCatResponse, getTaxnomyAddCat, TaxonomyRecommendationRequest, TaxonomyRecommendationResponse, getTaxonomyRecommendations } from "@tCentro/Services/CENTaxonomyAddCatService";
import { getTaxonomyCategoryById, Category } from "@tCentro/Services/CENTaxonomyAddCatService";



type HandlerType = ApiHandler<
    addCatRequest & HasOnSuccess<addCatResponse>,
    addCatResponse &  HasOnError
>;

type GetTaxonomyRecommendationHandlerType = ApiHandler<
    TaxonomyRecommendationRequest & HasOnSuccess<TaxonomyRecommendationResponse>,
    TaxonomyRecommendationResponse & HasOnError
>;

type HasOnErrorWithMessage = {
    onError?: (error: string) => void;
};

type GetTaxonomyCategoryByIdHandlerType = ApiHandler<
    { id: string } & HasOnSuccess<Category> & HasOnErrorWithMessage,
    Category & { errorMessage?: string }
>;


export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = props.parameter; 
};


export const DispatchBeginAddTaxonomyCat: HandlerType = props => {
    (props as any).dispatch({
        type: "Custom/addTaxonomyCat/BeginLoad",
    });
};


export const SendDataToApi: HandlerType = async props => {
    try {
        props.apiResult = await getTaxnomyAddCat(props.apiParameter);
    } catch (error) {
        if (isApiError(error)) {
            const errorMsg = error.errorJson?.message || error.errorMessage;

            if (errorMsg) {
                logger.error("Add Cat Taxonomy API error:", errorMsg);
                props.apiResult = { ...props.apiResult, errorMessage: errorMsg };
                return;
            }
        }

  
        logger.error("Unhandled Add Cat Taxonomy API error:", error);
        
    }
};
export const ExecuteOnErrorCallback: HandlerType = props => {
    if ( props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};




export const DispatchCompleteFreightQuote: HandlerType = props => {
    if (!props.apiResult) {
        return;
    }
    (props as any).dispatch({
        type: "Custom/addTaxonomyCat/CompletedLoad",
        result: props.apiResult,
    });
};


export const chain = [
    DispatchBeginAddTaxonomyCat,
    PopulateApiParameter,
    SendDataToApi,
     ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
    DispatchCompleteFreightQuote,
];


const addTaxonomyCatHandler = createHandlerChainRunner(chain, "addTaxonomyCatHandler");

export default addTaxonomyCatHandler;

// GET Taxonomy Recommendations Handler

export const PopulateGetTaxonomyRecommendationApiParameter: GetTaxonomyRecommendationHandlerType = props => {
    props.apiParameter = props.parameter;
};

export const DispatchBeginGetTaxonomyRecommendation: GetTaxonomyRecommendationHandlerType = props => {
    (props as any).dispatch({
        type: "Custom/getTaxonomyRecommendation/BeginLoad",
    });
};

export const SendGetTaxonomyRecommendationDataToApi: GetTaxonomyRecommendationHandlerType = async props => {
    try {
        const { onSuccess, onError, ...apiParams } = props.apiParameter;
        props.apiResult = await getTaxonomyRecommendations(apiParams);
    } catch (error) {
        if (isApiError(error)) {
            const errorMsg = error.errorJson?.message || error.errorMessage;

            if (errorMsg) {
                logger.error("Get Taxonomy Recommendation API error:", errorMsg);
                props.apiResult = { ...props.apiResult, errorMessage: errorMsg };
                return;
            }
        }

        logger.error("Unhandled Get Taxonomy Recommendation API error:", error);
    }
};

export const ExecuteGetTaxonomyRecommendationOnErrorCallback: GetTaxonomyRecommendationHandlerType = props => {
    if (props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecuteGetTaxonomyRecommendationOnSuccessCallback: GetTaxonomyRecommendationHandlerType = props => {
    if (!props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const DispatchCompleteGetTaxonomyRecommendation: GetTaxonomyRecommendationHandlerType = props => {
    if (!props.apiResult) {
        return;
    }
    (props as any).dispatch({
        type: "Custom/getTaxonomyRecommendation/CompletedLoad",
        result: props.apiResult,
    });
};

export const getTaxonomyRecommendationChain = [
    DispatchBeginGetTaxonomyRecommendation,
    PopulateGetTaxonomyRecommendationApiParameter,
    SendGetTaxonomyRecommendationDataToApi,
    ExecuteGetTaxonomyRecommendationOnErrorCallback,
    ExecuteGetTaxonomyRecommendationOnSuccessCallback,
    DispatchCompleteGetTaxonomyRecommendation,
];

const getTaxonomyRecommendationHandler = createHandlerChainRunner(getTaxonomyRecommendationChain, "getTaxonomyRecommendationHandler");

export { getTaxonomyRecommendationHandler };

// GET Taxonomy Category By Id Handler

export const PopulateGetTaxonomyCategoryByIdApiParameter: GetTaxonomyCategoryByIdHandlerType = props => {
    props.apiParameter = props.parameter;
};

export const DispatchBeginGetTaxonomyCategoryById: GetTaxonomyCategoryByIdHandlerType = props => {
    (props as any).dispatch({
        type: "Custom/getTaxonomyCategoryById/BeginLoad",
    });
};

export const SendGetTaxonomyCategoryByIdDataToApi: GetTaxonomyCategoryByIdHandlerType = async props => {
    try {
        const { onSuccess, onError, ...apiParams } = props.apiParameter;
        props.apiResult = await getTaxonomyCategoryById(apiParams.id);
    } catch (error) {
        if (isApiError(error)) {
            const errorMsg = error.errorJson?.message || error.errorMessage;

            if (errorMsg) {
                logger.error("Get Taxonomy Category By Id API error:", errorMsg);
                props.apiResult = { ...props.apiResult, errorMessage: errorMsg };
                return;
            }
        }

        logger.error("Unhandled Get Taxonomy Category By Id API error:", error);
    }
};

export const ExecuteGetTaxonomyCategoryByIdOnErrorCallback: GetTaxonomyCategoryByIdHandlerType = props => {
    if (props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecuteGetTaxonomyCategoryByIdOnSuccessCallback: GetTaxonomyCategoryByIdHandlerType = props => {
    if (!props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const DispatchCompleteGetTaxonomyCategoryById: GetTaxonomyCategoryByIdHandlerType = props => {
    if (!props.apiResult) {
        return;
    }
    (props as any).dispatch({
        type: "Custom/getTaxonomyCategoryById/CompletedLoad",
        result: props.apiResult,
    });
};

export const getTaxonomyCategoryByIdChain = [
    DispatchBeginGetTaxonomyCategoryById,
    PopulateGetTaxonomyCategoryByIdApiParameter,
    SendGetTaxonomyCategoryByIdDataToApi,
    ExecuteGetTaxonomyCategoryByIdOnErrorCallback,
    ExecuteGetTaxonomyCategoryByIdOnSuccessCallback,
    DispatchCompleteGetTaxonomyCategoryById,
];

const getTaxonomyCategoryByIdHandler = createHandlerChainRunner(getTaxonomyCategoryByIdChain, "getTaxonomyCategoryByIdHandler");

export { getTaxonomyCategoryByIdHandler };

// PATCH Taxonomy Category Handler

type PatchTaxonomyCategoryHandlerType = ApiHandler<
    addCatRequest & HasOnSuccess<addCatResponse> & HasOnErrorWithMessage,
    addCatResponse & { errorMessage?: string }
>;

export const PopulatePatchTaxonomyCategoryApiParameter: PatchTaxonomyCategoryHandlerType = props => {
    props.apiParameter = props.parameter;
};

export const DispatchBeginPatchTaxonomyCategory: PatchTaxonomyCategoryHandlerType = props => {
    (props as any).dispatch({
        type: "Custom/patchTaxonomyCategory/BeginLoad",
    });
};

export const SendPatchTaxonomyCategoryDataToApi: PatchTaxonomyCategoryHandlerType = async props => {
    try {
        const { patchTaxonomy } = await import("@tCentro/Services/CENTaxonomyAddCatService");
        props.apiResult = await patchTaxonomy(props.apiParameter);
    } catch (error) {
        if (isApiError(error)) {
            const errorMsg = error.errorJson?.message || error.errorMessage;

            if (errorMsg) {
                logger.error("Patch Taxonomy Category API error:", errorMsg);
                props.apiResult = { ...props.apiResult, errorMessage: errorMsg };
                return;
            }
        }

        logger.error("Unhandled Patch Taxonomy Category API error:", error);
    }
};

export const ExecutePatchTaxonomyCategoryOnErrorCallback: PatchTaxonomyCategoryHandlerType = props => {
    if (props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecutePatchTaxonomyCategoryOnSuccessCallback: PatchTaxonomyCategoryHandlerType = props => {
    if (!props.apiResult?.errorMessage) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const DispatchCompletePatchTaxonomyCategory: PatchTaxonomyCategoryHandlerType = props => {
    if (!props.apiResult) {
        return;
    }
    (props as any).dispatch({
        type: "Custom/patchTaxonomyCategory/CompletedLoad",
        result: props.apiResult,
    });
};

export const patchTaxonomyCategoryChain = [
    DispatchBeginPatchTaxonomyCategory,
    PopulatePatchTaxonomyCategoryApiParameter,
    SendPatchTaxonomyCategoryDataToApi,
    ExecutePatchTaxonomyCategoryOnErrorCallback,
    ExecutePatchTaxonomyCategoryOnSuccessCallback,
    DispatchCompletePatchTaxonomyCategory,
];

const patchTaxonomyCategoryHandler = createHandlerChainRunner(patchTaxonomyCategoryChain, "patchTaxonomyCategoryHandler");

export { patchTaxonomyCategoryHandler };
